import React from 'react';
import { getDataTypeLabels } from '../lib/pricingCatalogStore';
import StepHero from './StepHero';
import { formatMultiplierAsPct } from '../lib/format';

function sizeMultiplier(samples) {
  if (samples >= 100000) return { mult: 1.2, bucket: '100K+ (best)' };
  if (samples >= 50000) return { mult: 0.98, bucket: '50K–99K' };
  if (samples >= 10000) return { mult: 0.98, bucket: '10K–49K' };
  if (samples >= 2000) return { mult: 0.95, bucket: '2K–9K' };
  if (samples >= 500) return { mult: 0.92, bucket: '500–1,999' };
  if (samples >= 100) return { mult: 0.9, bucket: '100–499' };
  return { mult: 0.75, bucket: '< 100' };
}

function followUpMultiplier(years) {
  if (years >= 5) return 1.04;
  return 1.0;
}

function formatModifierTag(value) {
  const pct = formatMultiplierAsPct(value);
  return pct ?? '+0%';
}

const DIVERSITY_LABELS = { 1: 'Single ancestry', 2: 'Narrow', 3: 'Mixed', 4: 'Diverse', 5: 'Highly diverse' };

export default function StepSpecifications({ inputs, onChange, onToggleDataType, glowField }) {
  const dataTypeLabels = getDataTypeLabels();
  const selected = new Set(inputs.dataTypes || []);
  const hasSamples = Number.isFinite(inputs.samples) && inputs.samples >= 1;
  const hasFollowUp = Number.isFinite(inputs.followUpYears) && inputs.followUpYears >= 0;
  const hasDiversity = Number.isFinite(inputs.populationDiversity);
  const size = hasSamples ? sizeMultiplier(inputs.samples) : null;
  const followUp = hasFollowUp ? followUpMultiplier(inputs.followUpYears) : 1.0;
  const diversityLabel = hasDiversity ? (DIVERSITY_LABELS[inputs.populationDiversity] || '—') : 'Not set';

  return (
    <section data-testid="step-specs">
      <StepHero
        sectionLabel="Specifications"
        title={<>How big, how deep, <em style={emStyle}>how rich.</em></>}
        lede="Sample size, modalities, follow-up duration, and the diversity of your cohort. Multipliers compound the base value — depth (more data types) and breadth (more samples) move the number most."
      />

      <div style={twoColStyle}>
        <Field label="Number of samples" glow={glowField === 'samples'}>
          <TextInput
            value={inputs.samples}
            suffix="patients"
            ariaLabel="Number of samples"
            placeholder="Enter cohort size"
            glow={glowField === 'samples'}
            onChange={(v) => onChange('samples', v === '' ? null : Math.max(0, parseInt(v, 10) || 0))}
          />
          <div style={hasSamples ? helperAccentStyle : helperMutedStyle}>
            {hasSamples
              ? `${size.bucket} bucket · ${formatMultiplierAsPct(size.mult)} adjustment${inputs.samples < 100000 ? ' (+20% unlocks at 100K+)' : ''}`
              : 'Enter the patient count to see the size band.'}
          </div>
        </Field>

        <Field label="Average follow-up · optional" glow={glowField === 'followUpYears'}>
          <FollowUpYearsInput
            value={inputs.followUpYears}
            ariaLabel="Follow-up years"
            placeholder="Optional — years of follow-up"
            glow={glowField === 'followUpYears'}
            onChange={(v) => onChange('followUpYears', v)}
          />
          <div style={helperMutedStyle}>
            {!hasFollowUp
              ? 'Leave blank if unknown or no follow-up. Enter 0 for none, or decimals such as 0.5.'
              : inputs.followUpYears >= 5
              ? `5+ yr window · ${formatMultiplierAsPct(followUp)} adjustment`
              : `${formatFollowUpYears(inputs.followUpYears)} · no bonus (+4% unlocks at 5+ yrs)`}
          </div>
        </Field>
      </div>

      <div style={{ marginTop: '32px' }}>
        <div style={fieldLabelStyle}>Data modalities · optional, select all that apply</div>
        <div style={pillRowStyle}>
          {dataTypeLabels.map((dt) => {
            const on = selected.has(dt.value);
            return (
              <button
                key={dt.value}
                type="button"
                onClick={() => onToggleDataType(dt.value)}
                style={on ? pillOnStyle : pillStyle}
                aria-pressed={on}
              >
                {dt.label.split(' (')[0]}
                <span style={on ? pillTagOnStyle : pillTagStyle}>{formatModifierTag(dt.multiplier)}</span>
              </button>
            );
          })}
        </div>
        <div style={selected.size > 0 ? helperAccentStyle : helperMutedStyle}>
          {selected.size === 0
            ? 'No modalities selected (no multiplier applied).'
            : `${selected.size} ${selected.size === 1 ? 'modality' : 'modalities'} selected`}
        </div>
      </div>

      <div style={{ marginTop: '32px' }}>
        <div style={fieldLabelStyle}>Population diversity</div>
        <div style={glowField === 'populationDiversity' ? diversityWrapGlowStyle : diversityWrapStyle}>
          <div style={diversityRowStyle}>
            <input
              type="range"
              min={1}
              max={5}
              step={1}
              value={hasDiversity ? inputs.populationDiversity : 1}
              onChange={(e) => onChange('populationDiversity', Number(e.target.value))}
              aria-label="Population diversity"
              style={hasDiversity ? diversitySliderStyle : diversitySliderGlowStyle}
            />
            <div style={diversityValueStyle}>
              {hasDiversity ? (
                <>
                  <span style={diversityNumStyle}>{inputs.populationDiversity}</span>
                  <span style={diversityMutedStyle}>/ 5 · {diversityLabel}</span>
                </>
              ) : (
                <span style={diversityMutedStyle}>Slide to set diversity</span>
              )}
            </div>
          </div>
          <div style={diversityScaleStyle}>
            <span>1 · Single ancestry</span>
            <span>3 · Mixed</span>
            <span>5 · Highly diverse</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div style={fieldLabelStyle}>{label}</div>
      {children}
    </div>
  );
}

function formatFollowUpYears(years) {
  if (!Number.isFinite(years)) return '—';
  if (years === 0) return '0 years';
  return Number.isInteger(years) ? `${years}-year window` : `${years} years`;
}

function sanitizeDecimalInput(raw) {
  let s = raw.replace(/[^0-9.]/g, '');
  const dot = s.indexOf('.');
  if (dot !== -1) {
    s = `${s.slice(0, dot + 1)}${s.slice(dot + 1).replace(/\./g, '')}`;
  }
  return s;
}

function FollowUpYearsInput({ value, onChange, ariaLabel, placeholder, glow }) {
  const [text, setText] = React.useState(() => formatFollowUpInputValue(value));

  React.useEffect(() => {
    setText(formatFollowUpInputValue(value));
  }, [value]);

  function handleChange(raw) {
    const next = sanitizeDecimalInput(raw);
    setText(next);
    if (next === '' || next === '.') {
      onChange(null);
      return;
    }
    const parsed = parseFloat(next);
    if (!Number.isFinite(parsed) || parsed < 0) return;
    if (!next.endsWith('.')) {
      onChange(parsed);
    }
  }

  return (
    <div style={glow ? textInputGlowStyle : textInputStyle}>
      <input
        type="text"
        inputMode="decimal"
        value={text}
        placeholder={placeholder}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={() => {
          if (text === '.' || text === '') {
            setText('');
            onChange(null);
            return;
          }
          const parsed = parseFloat(text);
          if (Number.isFinite(parsed) && parsed >= 0) {
            onChange(parsed);
            setText(formatFollowUpInputValue(parsed));
          }
        }}
        aria-label={ariaLabel}
        style={textInputInnerStyle}
      />
      <span style={suffixStyle}>years</span>
    </div>
  );
}

function formatFollowUpInputValue(value) {
  if (value === null || value === undefined) return '';
  return String(value);
}

function TextInput({ value, suffix, onChange, ariaLabel, placeholder, glow }) {
  // value can be null on "Start fresh" — render the input as empty
  // until the user types something.
  const displayValue =
    value === null || value === undefined ? ''
      : Number(value).toLocaleString('en-US');
  return (
    <div style={glow ? textInputGlowStyle : textInputStyle}>
      <input
        type="text"
        inputMode="numeric"
        value={displayValue}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ''))}
        aria-label={ariaLabel}
        style={textInputInnerStyle}
      />
      {suffix && <span style={suffixStyle}>{suffix}</span>}
    </div>
  );
}

const emStyle = { fontStyle: 'italic', color: 'var(--accent)' };
const twoColStyle = { marginTop: '32px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' };

const fieldLabelStyle = { fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' };
const helperAccentStyle = { marginTop: '10px', fontSize: '11.5px', color: 'var(--accent)' };
const helperMutedStyle = { marginTop: '10px', fontSize: '11.5px', color: 'var(--muted)' };

const textInputStyle = {
  height: '44px',
  padding: '0 14px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'var(--line)',
  borderRadius: '4px',
  background: 'var(--surface)',
};
const textInputGlowStyle = {
  ...textInputStyle,
  borderColor: 'var(--amber)',
  boxShadow: '0 0 0 4px rgba(245, 158, 11, 0.18)',
};
const textInputInnerStyle = {
  flex: 1,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontSize: '14px',
  fontFamily: 'var(--font)',
  fontVariantNumeric: 'tabular-nums',
  color: 'var(--ink)',
};
const suffixStyle = { color: 'var(--muted)', fontSize: '12px', marginLeft: '8px' };

const pillRowStyle = { display: 'flex', flexWrap: 'wrap', gap: '6px' };
const pillBase = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  height: '36px',
  padding: '0 14px',
  borderRadius: '999px',
  fontSize: '12.5px',
  cursor: 'pointer',
  fontFamily: 'var(--font)',
};
const pillStyle = { ...pillBase, background: 'transparent', color: 'var(--ink)', border: '1px solid var(--line)' };
const pillOnStyle = { ...pillBase, background: 'var(--accent-bg)', color: 'var(--accent)', border: '1px solid var(--accent)', fontWeight: 500 };

const pillTagStyle = { display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: '999px', background: 'var(--line-dim)', color: 'var(--ink2)', fontSize: '11px', fontWeight: 500 };
const pillTagOnStyle = { ...pillTagStyle, background: 'rgba(63, 107, 79, 0.15)', color: 'var(--accent)', fontWeight: 600 };

const diversityWrapStyle = {
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'transparent',
  borderRadius: '8px',
  padding: '8px',
  margin: '-8px',
};
const diversityWrapGlowStyle = {
  ...diversityWrapStyle,
  borderColor: 'var(--amber)',
  boxShadow: '0 0 0 4px rgba(245, 158, 11, 0.18)',
};
const diversityRowStyle = { display: 'flex', alignItems: 'center', gap: '16px' };
const diversitySliderStyle = { flex: 1, accentColor: 'var(--accent)' };
const diversitySliderGlowStyle = { flex: 1, accentColor: 'var(--amber)' };
const diversityValueStyle = { display: 'flex', alignItems: 'baseline', gap: '6px', width: '160px', justifyContent: 'flex-end' };
const diversityNumStyle = { fontFamily: 'var(--display)', fontStyle: 'italic', fontSize: '24px', color: 'var(--accent)', fontWeight: 500 };
const diversityMutedStyle = { color: 'var(--muted)', fontSize: '13px' };
const diversityScaleStyle = { display: 'flex', justifyContent: 'space-between', fontSize: '11.5px', color: 'var(--muted)', marginTop: '6px' };
